import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { Empty, ErrorState, Loading } from '@/ui/states'

type SpendData = Schema<'ModuleSpendOut'>
type Cap = Schema<'SpendCapOut'>
type Module = SpendData['module']

/**
 * What each module calls itself on this panel.
 *
 * The only thing that differs between the outreach copy and the SEO copy, which
 * is why there is one component: every sentence about the *cap* is about the
 * organisation and reads identically on both screens, because there is one cap.
 */
const COPY: Record<Module, { label: string; nothingYet: string }> = {
  outreach: {
    label: 'Outreach',
    nothingYet:
      'Once discovery runs and messages start going out, this shows what outreach cost this month and how it sits against your organisation\u2019s budget cap.',
  },
  seo: {
    label: 'SEO',
    nothingYet:
      'Once audits, keyword research and content generation start running, this shows what SEO cost this month and how it sits against your organisation\u2019s budget cap.',
  },
  platform: {
    label: 'Platform',
    nothingYet:
      'Nothing has been metered against the platform this month. Service email and digests appear here when they are sent.',
  },
}

/**
 * Spend against cap — S-B31c's outreach panel and S-B32b's *Budget burn* card.
 *
 * **One component for two screens, because there is one cap.** S-B31c wrote this
 * for outreach and every argument below turned out to be about the product
 * rather than about outreach; `app/v1/spend.py` says the same thing about the
 * endpoint it reads. The module decides two sentences of copy and the path, and
 * nothing else.
 *
 * **The cap on this panel belongs to the organisation, and the panel says so out
 * loud.** There is no per-module ceiling in AugRank: the pre-flight in
 * `app/metering/service.py` checks one number for every module, so a meter drawn
 * as *"outreach: 41% of its cap"* would promise that outreach stops at 100%,
 * and it does not — it stops when the *organisation* does, which can happen with
 * outreach barely started because content generation spent the month. So the
 * meter is the organisation's, and outreach's figure sits beside it with its
 * share stated. `app/metering/attribution.py` carries the full argument.
 *
 * **The lines are not a column that adds up, and they are not drawn as one.**
 * Every figure is rounded up once over its own exact sum (`ledger.to_minor`), so
 * the lines can exceed the total by a paise apiece. Each therefore gets its own
 * share bar and there is no total row underneath — the same rule
 * `app/db/metering.meter_totals` already states for `admin#usage`.
 *
 * **Nothing here is computed.** Every rupee figure, percentage and reset date
 * arrives formatted from the endpoint, because the alternative is a second
 * implementation of money formatting in TypeScript that will disagree with the
 * invoice. `share` is `null` rather than `0` when the module spent nothing, and
 * renders as no bar rather than an empty one.
 *
 * **Its own query, like the other three.** A ledger read that fails leaves the
 * funnel, account health and deliverability on screen with their own data.
 */
interface SpendPanelProps {
  module: Module
  /**
   * SEO-UI-23, then parity (2026-09-13). One card either way now; the prop is
   * kept for the SEO dashboard's grid, where the card is a cell beside *Core
   * Web Vitals*, and is what the two callers already pass.
   */
  framed?: boolean
}

/**
 * The two shapes the mockups draw, one per module (parity P-SDASH-05 and
 * P-ODASH-07, 2026-09-13).
 *
 * `seo.html:155-174` is *Budget burn*: a header with the reset date, one
 * *This month · ₹spent / ₹cap* row over the organisation's meter, then a
 * transparent table of lines — label · quantity · amount.
 *
 * `outreach.html:187-202` is *Spend against cap*: the same header, then the
 * lines as a three-column grid, each with its label, its amount as an `h2`
 * figure and its own share bar.
 *
 * What the mockups do not draw but the product knows stays, under the drawn
 * blocks and only when it is true: the cap being reached or paused, the
 * overage due, and the organisation having no cap at all — each of those is a
 * state, not decoration. The *All modules* total is the one line that makes
 * the meter honest, since the cap is the organisation's and the lines are the
 * module's, so it is printed under the meter in a sentence.
 */
export function SpendPanel({ module, framed = false }: SpendPanelProps): JSX.Element {
  // Two literal paths rather than one interpolated string: the generated client
  // types each path against its own response, and a template literal would put
  // `never` on both sides of the union. They answer the same model, which is why
  // the branch ends here rather than running through the component.
  const spend = useQuery(
    (signal) =>
      module === 'seo'
        ? call(() => api.GET('/api/v1/seo/spend', { signal }))
        : call(() => api.GET('/api/v1/outreach/spend', { signal })),
    [module],
  )
  const copy = COPY[module]
  const title = module === 'seo' ? 'Budget burn' : 'Spend against cap'
  void framed

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>{title}</h2>
        <span className="ar-card__spacer" />
        {spend.state.status === 'ready' && !spend.state.data.empty ? (
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
            {/* `T00:00:00Z` appended, which is the spelling `UsagePage` already
                uses for this same `resets_on` field. `resets_on` is a bare
                `YYYY-MM-DD` and `new Date` of one is ambiguous; what matters more
                than the choice is that both screens make the *same* one, because
                two reset dates for one tenant is a worse bug than either. */}
            Resets {formatDate(`${spend.state.data.resets_on}T00:00:00Z`)}
          </span>
        ) : null}
      </div>
      <div className="ar-card__body ar-stack">
        {spend.state.status === 'loading' ? (
          <Loading label={`Loading your ${copy.label.toLowerCase()} spend`} lines={3} />
        ) : null}
        {spend.state.status === 'error' ? (
          <ErrorState
            failure={spend.state.failure}
            title="Could not load your spend"
            onRetry={spend.refetch}
          />
        ) : null}
        {spend.state.status === 'ready' ? (
          spend.state.data.empty ? (
            <Empty
              icon="₹"
              title="Nothing has been metered yet"
              body={nothingYet(spend.state.data, module)}
              action={<CapAction data={spend.state.data} size="sm" />}
            />
          ) : module === 'seo' ? (
            <BudgetBurn data={spend.state.data} />
          ) : (
            <Columns data={spend.state.data} />
          )
        ) : null}
      </div>
    </section>
  )
}

/**
 * The empty state's one action. *Set a budget cap* was offered whether or not
 * one was set — beside a sidebar meter reading ₹1.28 of ₹5,000 (walkthrough
 * 2026-09-12). A cap that exists is something to *review*, and the sentence
 * under the title names it so the two screens agree.
 */
function CapAction({ data, size }: { data: SpendData; size?: 'sm' }): JSX.Element {
  const has = typeof data.cap.cap_minor === 'number'
  return (
    <Link className={`ar-btn ar-btn--ghost${size ? ` ar-btn--${size}` : ''}`} to="/settings/budget">
      {has ? 'Review the budget cap' : 'Set a budget cap'}
    </Link>
  )
}

function nothingYet(data: SpendData, module: Module): string {
  const base = COPY[module].nothingYet
  return typeof data.cap.cap_display === 'string'
    ? `${base} The cap is ${data.cap.cap_display} a month.`
    : base
}

/** `seo.html:155-174` — *This month ₹spent / ₹cap*, the meter, the lines as a table. */
function BudgetBurn({ data }: { data: SpendData }): JSX.Element {
  const cap = data.cap
  return (
    <>
      <div>
        <div className="ar-row app-spend__cap-head">
          <span>This month</span>
          <span className="ar-card__spacer" />
          <span className="ar-mono">
            {data.tenant_display}
            {cap.effective_cap_minor === null ? '' : ` / ${cap.effective_display ?? ''}`}
            {cap.raised ? ' (raised this month)' : ''}
          </span>
        </div>
        {cap.effective_cap_minor === null ? null : <CapMeter cap={cap} />}
        <p className="ar-subtle app-spend__note">
          <ModuleShare data={data} />
        </p>
      </div>
      <StateLines data={data} />
      <table className="ar-table app-spend__lines">
        <caption className="ar-visually-hidden">Where it went</caption>
        <tbody>
          {data.lines.map((line) => (
            <tr key={`${line.source}:${line.meter}`}>
              <td>{line.label}</td>
              <td className="ar-num ar-subtle">
                {line.quantity.toLocaleString()} {line.unit}
                {line.quantity === 1 ? '' : 's'}
              </td>
              <td className="ar-num">{line.display}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

/** `outreach.html:187-202` — one column per line: label, figure, share bar. */
function Columns({ data }: { data: SpendData }): JSX.Element {
  return (
    <>
      <div className="ar-grid ar-grid--3">
        {data.lines.map((line) => (
          <div key={`${line.source}:${line.meter}`}>
            <div className="ar-muted app-spend__label">{line.label}</div>
            <div className="ar-h2 ar-num">{line.display}</div>
            {/* No bar at all when there is nothing to be a share of — see `share`. */}
            {line.share === null ? null : (
              <div
                className="ar-meter"
                role="meter"
                aria-label={`${line.label}: ${String(line.share)}% of ${COPY[data.module].label.toLowerCase()} spend`}
                aria-valuenow={line.share}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ marginTop: 'var(--ar-space-2)' }}
              >
                <div
                  className="ar-meter__fill"
                  style={{ width: `${String(Math.min(100, line.share))}%` }}
                />
              </div>
            )}
            <div className="ar-subtle app-spend__qty">
              {line.quantity.toLocaleString()} {line.unit}
              {line.quantity === 1 ? '' : 's'}
            </div>
          </div>
        ))}
      </div>
      <p className="ar-subtle app-spend__note">
        <ModuleShare data={data} />
      </p>
      <StateLines data={data} />
    </>
  )
}

/**
 * The one sentence that keeps the meter honest: the cap is the organisation's
 * and the lines are the module's, so both totals are named.
 */
function ModuleShare({ data }: { data: SpendData }): JSX.Element {
  const label = COPY[data.module].label
  return (
    <>
      {label} spent {data.module_display} of the organisation&rsquo;s {data.tenant_display}
      {data.share_of_tenant === null ? '' : ` (${data.share_of_tenant.toFixed(1)}%)`}.
    </>
  )
}

/** The states the mockups do not draw, shown only when they are true. */
function StateLines({ data }: { data: SpendData }): JSX.Element | null {
  const cap = data.cap
  const noCap = cap.effective_cap_minor === null
  if (!noCap && !cap.reached && data.overage_due_minor === null) return null
  return (
    <div className="ar-stack ar-stack--tight">
      {noCap ? (
        <p className="ar-muted app-spend__nocap">
          No budget cap is set for this organisation, so nothing stops on spend.{' '}
          <Link to="/settings/budget">Set one in Settings</Link> to have automation pause before a
          month runs away.
        </p>
      ) : null}
      {cap.reached ? (
        <p className="ar-subtle app-spend__note">
          <CapSentence cap={cap} />
        </p>
      ) : null}
      <Overage data={data} />
    </div>
  )
}

function CapMeter({ cap }: { cap: Cap }): JSX.Element {
  return (
    <div
      className={`ar-meter ${cap.reached ? 'ar-meter--over' : ''}`}
      role="meter"
      aria-label={`Spend against the organisation's cap: ${String(cap.percent_used ?? 0)}%`}
      aria-valuenow={cap.percent_used ?? 0}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{ marginTop: 'var(--ar-space-2)' }}
    >
      <div
        className="ar-meter__fill"
        style={{ width: `${String(Math.min(100, cap.percent_used ?? 0))}%` }}
      />
    </div>
  )
}

function CapSentence({ cap }: { cap: Cap }): JSX.Element {
  if (cap.paused) {
    return (
      <>
        <strong>Automation is paused</strong> — the organisation has reached its cap for this month.{' '}
        <Link to="/settings/budget">Raise it for this month</Link>, or wait for the period to roll
        over.
      </>
    )
  }
  if (cap.reached) {
    return (
      <>
        <strong>The cap has been reached and work is continuing</strong>, because pausing at the cap
        is switched off. Everything above the cap is billed as overage.
      </>
    )
  }
  return (
    <>
      {cap.remaining_display} left of {cap.effective_display} this month, across every module.
    </>
  )
}

/**
 * The overage line — S-B30's derivation, shown before the invoice exists.
 *
 * Deliberately organisation-wide and not apportioned to the module: the markup
 * is charged on what the *organisation* spent above its standing cap, and
 * splitting it across modules would be a number nobody invoices.
 */
function Overage({ data }: { data: SpendData }): JSX.Element | null {
  if (data.overage_due_minor === null) return null
  return (
    <p className="ar-subtle app-spend__overage">
      <span className="ar-badge ar-badge--warning">Overage</span> {data.overage_due_display} will be
      invoiced for this period — what the organisation spent above its standing cap, plus 20%.
    </p>
  )
}
